import { Box, Typography } from '@mui/material'
import { CheckCircleOutline, ErrorOutline, InfoOutlined } from '@mui/icons-material'
import { DocumentRequirementCard } from './DocumentRequirementCard'
import { colors } from './styles'
import { isSatisfied, type AddDocumentHandler, type DocumentRequirements } from './types'

interface DocumentRequirementListProps {
  data: DocumentRequirements
  onAdd: AddDocumentHandler
  onOpenDocument?: (documentID: number) => void
  missingMessage?: string
  satisfiedMessage?: string
  emptyMessage?: string
}

function Banner (props: { tone: 'missing' | 'complete' | 'empty', message: string, testId: string }): JSX.Element {
  const tone = {
    missing: { bg: colors.redLight, border: colors.red, icon: <ErrorOutline sx={{ color: colors.red }} /> },
    complete: { bg: colors.greenLight, border: colors.green, icon: <CheckCircleOutline sx={{ color: colors.green }} /> },
    empty: { bg: colors.cardBackground, border: colors.border, icon: <InfoOutlined sx={{ color: colors.textTertiary }} /> }
  }[props.tone]

  return <Box
    role={props.tone === 'missing' ? 'alert' : 'status'}
    data-testid={props.testId}
    sx={{ display: 'flex', alignItems: 'center', gap: '20px', px: '20px', py: '12px', bgcolor: tone.bg, border: `1px solid ${tone.border}`, borderRadius: '10px' }}
  >
    {tone.icon}
    <Typography sx={{ flex: 1, fontSize: 12, lineHeight: 1.3, color: colors.textPrimary }}>{props.message}</Typography>
  </Box>
}

/**
 * Presentational list of document types for one child, with the documents on file for each and an add flow.
 * Data loading and saving belong to the host (see useServiceDocuments for the caregiver-portal service host),
 * so this can be reused anywhere a set of document types needs to be shown for a child.
 */
export function DocumentRequirementList (props: DocumentRequirementListProps): JSX.Element {
  const { data } = props
  const missing = data.requirements.some(r => !isSatisfied(r))

  if (data.requirements.length === 0) {
    return <Banner tone='empty' testId='documentsNone' message={props.emptyMessage ?? 'No documents are required.'} />
  }

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {missing
      ? <Banner tone='missing' testId='documentsMissing' message={props.missingMessage ?? 'Required documents are missing.'} />
      : <Banner tone='complete' testId='documentsComplete' message={props.satisfiedMessage ?? 'All required documents have been uploaded.'} />}
    {data.requirements.map(requirement =>
      <DocumentRequirementCard
        key={requirement.documentTypeID}
        requirement={requirement}
        child={data.child}
        canAdd={data.canAddDocuments}
        onAdd={props.onAdd}
        onOpenDocument={props.onOpenDocument}
      />
    )}
  </Box>
}
